import React, { useState, useEffect } from 'react';
import { Phone, X, MessageCircle } from 'lucide-react';
import { trackEvent } from '../services/analytics';
import { useLocation } from 'react-router-dom';

// WhatsApp Business Number (India +91)
const WA_NUMBER = '919217665391';
const PHONE_NUMBER = '919217665391';
const WA_MSG = encodeURIComponent('Hi Taraanaya! I want to know more about my cosmic story.');

// Pages that have a bottom navigation bar (need extra offset)
const BOTTOM_NAV_PATHS = ['/domain-insights', '/domain-story', '/event-timeline', '/credits-refill', '/profile'];
// Pages where WhatsApp/phone float would overlap core UI (hide on these)
// /dashboard = Future Story Reels with 3D solar system + Coming Soon cards — bubble overlaps cards
const HIDDEN_PATHS = ['/onboarding', '/onboarding-new', '/onboarding-revolutionary', '/dashboard'];

const FloatingContact = () => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  
  // All hooks MUST be called unconditionally before any early return
  // Close expanded state on route change
  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);
  
  // Hide on onboarding flow — WhatsApp bubble would sit over "See Your Story" CTA
  if (HIDDEN_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return null;
  }
  
  // Detect if current page has bottom nav bar
  const hasBottomNav = BOTTOM_NAV_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  
  // Bottom offset: clear bottom nav (72px + 16px gap) on dashboard pages, else standard.
  // On iOS the home indicator (~34px) sits at the very bottom — we add safe-area inset
  // so the bubble doesn't disappear behind the indicator on iPhone X and newer.
  const bottomOffset = hasBottomNav ? 92 : 24;

  return (
    <div data-testid="floating-contact" style={{
      position: 'fixed',
      bottom: `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))`,
      right: `calc(16px + env(safe-area-inset-right, 0px))`,
      zIndex: 9990,
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'flex-end', 
      gap: 10,
      pointerEvents: 'none',
    }}>
      {/* Expanded buttons */}
      {expanded && (
        <>
          {/* Phone Call */}
          <a
            href={`tel:+${PHONE_NUMBER}`}
            data-testid="floating-phone-btn"
            onClick={() => trackEvent('phone_click', { page: location.pathname })}
            style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg, #edc161, #d4a84b)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(237,193,97,.35)',
              textDecoration: 'none', cursor: 'pointer',
              animation: 'fcFloatIn 0.25s ease-out',
              pointerEvents: 'auto',
            }}
          >
            <Phone size={18} color="#020712" strokeWidth={2.5} />
          </a>

          {/* WhatsApp */}
          <a
            href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="floating-whatsapp-btn"
            onClick={() => trackEvent('whatsapp_click', { page: location.pathname })}
            style={{
              width: 44, height: 44, borderRadius: '50%',
              background: '#25D366',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(37,211,102,.35)',
              textDecoration: 'none', cursor: 'pointer',
              animation: 'fcFloatIn 0.2s ease-out',
              pointerEvents: 'auto',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        </>
      )}

      {/* Toggle FAB - smaller, subtle */}
      <button
        data-testid="floating-contact-toggle"
        onClick={() => {
          setExpanded(prev => !prev);
          trackEvent('contact_fab_toggle', { page: location.pathname, expanded: !expanded });
        }}
        style={{
          width: 48, height: 48, borderRadius: '50%',
          background: expanded
            ? 'linear-gradient(135deg, #374151, #1f2937)'
            : 'linear-gradient(135deg, #25D366, #128C7E)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: 'none', cursor: 'pointer',
          boxShadow: expanded
            ? '0 4px 16px rgba(0,0,0,.4)'
            : '0 4px 20px rgba(37,211,102,.4)',
          transition: 'all 0.3s ease',
          pointerEvents: 'auto',
        }}
      >
        {expanded
          ? <X size={20} color="#fff" strokeWidth={2.5} />
          : <MessageCircle size={20} color="#fff" strokeWidth={2.5} />
        }
      </button>

      <style>{`
        @keyframes fcFloatIn {
          from { opacity: 0; transform: translateY(10px) scale(0.8); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default FloatingContact;
